import type {
  ExtendedResource,
  ExtendedResourceConstructor,
} from '@seen/core/ExtendedResource'
import {
  getSearchKey,
  normalizeQuery,
  type QueryTypes,
} from '@seen/core/search'
import { skipToken, type UseQueryResult } from '@tanstack/react-query'
import { NO_QUERY } from '#src/constants/no-query.ts'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useErrorToast } from '#src/hooks/useErrorToast.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { useQuery } from '#src/hooks/useQuery.ts'

interface UseSearchResourcePagesUntilOptions<T> {
  // Checked after each page against everything fetched so far.
  enough: (resources: T[]) => boolean
  maxPages: number
}

// Pages through a search until `enough` holds, the results run out, or
// `maxPages` pages have been read, for callers that need a few distinct rows
// out of a result set that can repeat one row many times. The cache key holds
// the query but not `enough`, so two callers with the same query must agree on
// when to stop.
export function useSearchResourcePagesUntil<T extends ExtendedResource>(
  resourceClass: ExtendedResourceConstructor<T>,
  query: QueryTypes | undefined,
  { enough, maxPages }: UseSearchResourcePagesUntilOptions<T>
): UseQueryResult<T[]> {
  const medplum = useMedplum()
  const parameters = normalizeQuery(query)
  const searchKey = query
    ? getSearchKey(medplum, resourceClass.RESOURCE_TYPE, parameters)
    : NO_QUERY

  const result = useQuery({
    queryKey: cacheKeys.searchResourcePages(
      resourceClass.RESOURCE_TYPE,
      searchKey === NO_QUERY ? NO_QUERY : `${searchKey}|until:${maxPages}`
    ),
    queryFn:
      searchKey === NO_QUERY
        ? skipToken
        : async () => {
            const byId = new Map<string, T>()
            let pages = 0
            for await (const page of medplum.searchResourceClassPages(
              resourceClass,
              parameters
            )) {
              for (const resource of page) {
                if (!byId.has(resource.id)) {
                  byId.set(resource.id, resource)
                }
              }
              pages += 1
              if (pages >= maxPages || enough([...byId.values()])) {
                break
              }
            }
            return [...byId.values()]
          },
    meta: { queryParams: parameters, resourceClass },
  })

  useErrorToast(
    result.error &&
      !(result.error instanceof Error && result.error.name === 'AbortError') &&
      `Error accessing ${searchKey}: ${result.error.message ?? 'Unknown error'}`
  )

  return result
}
